import { useMemo, useState } from 'react';
import { useCommunityPicker } from '../lib/useCommunityPicker';
import { useCanModerateCommunity } from '../lib/useCanModerateCommunity';
import { useCommunityCreatorIdentity } from '../api/useCommunityCreatorIdentity';
import { Community } from '../model/community.types';
import { CommunitiesHeader } from './CommunitiesHeader';
import { CommunityFeed } from './CommunityFeed';
import { CommunityModerationPanel } from './CommunityModerationPanel';
import { CommunityModerationSummary } from './CommunityModerationSummary';
import { CommunityToolbar } from './CommunityToolbar';
import { NewPostField } from './NewPostField';
import { PinnedPostsSection } from './PinnedPostsSection';

type Props = {
  communities: Community[];
  influencerId?: string;
};

export const CommunitiesContent = ({ communities, influencerId }: Props) => {
  const communityIds = useMemo(
    () => communities.map((community) => community.id),
    [communities],
  );
  const { activeId } = useCommunityPicker(communityIds);
  const canModerate = useCanModerateCommunity(influencerId);
  const creatorIdentity = useCommunityCreatorIdentity(influencerId);
  const activeCommunity = communities.find(
    (community) => community.id === activeId,
  );

  const [isModerationOpen, setModerationOpen] = useState(false);

  return (
    <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-7">
      <CommunitiesHeader onModeration={() => setModerationOpen(true)} />
      {activeCommunity && (
        <div className="flex flex-col gap-4">
          <CommunityToolbar membersCount={activeCommunity.membersCount} />
          <div className="flex flex-col gap-4 lg:flex-row">
            <div className="flex min-w-0 flex-1 flex-col gap-4">
              <PinnedPostsSection
                communityId={activeCommunity.id}
                influencerId={influencerId}
              />
              {canModerate && (
                <NewPostField
                  communityId={activeCommunity.id}
                  author={creatorIdentity}
                  influencerId={influencerId}
                  memberCount={activeCommunity.membersCount}
                />
              )}
              <CommunityFeed
                communityId={activeCommunity.id}
                influencerId={influencerId}
              />
            </div>
            <CommunityModerationSummary community={activeCommunity} />
          </div>
        </div>
      )}
      <CommunityModerationPanel
        communities={communities}
        isOpen={isModerationOpen}
        onClose={() => setModerationOpen(false)}
      />
    </div>
  );
};
